
import { useState } from "react";
import { Book } from "lucide-react";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";

interface HeroAwakeningProps {
  visible: boolean;
}

export function HeroAwakening({ visible }: HeroAwakeningProps) {
  const [awakeningHovered, setAwakeningHovered] = useState(false);

  return (
    <div className="mt-10 md:mt-14 space-y-4 mb-6 md:mb-8 px-3 sm:px-0">
      <h4 className="text-xl sm:text-2xl md:text-2xl font-bold text-charcoal mb-6 md:mb-8">
        These questions aren't signs of failure.
      </h4>
      <div 
        className="relative"
        onMouseEnter={() => setAwakeningHovered(true)}
        onMouseLeave={() => setAwakeningHovered(false)}
      >
        <p 
          className={cn(
            "text-lg sm:text-xl md:text-2xl font-serif italic mb-6 md:mb-8 transition-all duration-500",
            awakeningHovered 
              ? "text-[#8B5CF6] transform scale-105" 
              : "text-charcoal/90"
          )}
        >
          <span className="relative inline-block">
            They're evidence of a deeper awakening.
            {awakeningHovered && (
              <span className="absolute -inset-1 -z-10 rounded-lg bg-[#8B5CF6]/10 animate-pulse" />
            )}
          </span>
        </p>
      </div>
      
      <p className="text-base sm:text-lg text-charcoal/80 leading-relaxed mx-auto max-w-md bg-offwhite/80 p-4 sm:p-5 md:p-6 relative z-10 shadow-sm">
        The most accomplished leaders throughout history eventually faced the same crossroads—where conventional metrics of success no longer satisfy the soul's deeper longing.
      </p>
      
      <div className="relative py-5 md:py-7 mt-4">
        <div className="flex items-center justify-center mb-4">
          <Book className="h-5 w-5 text-charcoal/60 mr-2" />
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif text-charcoal/90 max-w-lg mx-auto leading-tight transition-all duration-500 font-bold">
            What if the answers you're seeking have already been found?
          </h3>
        </div>
        <Separator className="my-5 md:my-6 bg-charcoal/20 h-[1px] w-28 mx-auto" />
        <div className="max-w-lg mx-auto relative mb-6">
          <p className="text-base sm:text-lg text-charcoal/80 mb-6">
            For fifteen centuries, monastics perfected practices that answer our deepest questions—not by escaping the world, but by transforming how we engage with it.
          </p>
          
          <div className="flex justify-center items-center relative my-8">
            <div 
              className="w-[1px] h-14 bg-[#8B5CF6]/70 animate-pulse-subtle"
              style={{
                boxShadow: "0 0 8px rgba(139, 92, 246, 0.3)"
              }}
            ></div>
          </div>
          
          <p className="text-xl sm:text-2xl md:text-3xl font-serif font-light text-charcoal/40 italic mt-6 mb-8">
            There is an ancient and proven path that can help you to answer questions that resist easy answers.
          </p>
          
          <div className="flex items-center justify-center mt-10 mb-6">
            <p className="text-xl sm:text-2xl font-sans font-medium text-charcoal">
              What if you could be guided toward:
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
